import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { ref, set } from 'firebase/database';
import { database } from '../../firebase';
import { parseLinkedInCompanyId } from '../../utils/linkedInCompany';
import './LinkedInSetup.css';

export default function LinkedInSetupModal({ companyUID, companyId = '', hasAtsAccess, onClose, onSaved }) {
  useUiLanguage();
  const { t } = useTranslation('home');
  const { isRTL } = useLanguage();
  const [value, setValue] = useState(companyId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);
  const inputRef = useRef(null);
  const pendingRef = useRef(false);

  useEffect(() => {
    const previousFocus = document.activeElement;
    inputRef.current?.focus();
    return () => previousFocus?.focus?.();
  }, []);

  const close = () => { if (!pendingRef.current) onClose(); };
  const handleKeys = (event) => {
    if (event.key === 'Escape') { event.stopPropagation(); close(); }
    if (event.key !== 'Tab') return;
    const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled), input:not(:disabled), a[href]')];
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  };
  const save = async (event) => {
    event.preventDefault();
    if (pendingRef.current || !companyUID) return;
    const id = parseLinkedInCompanyId(value);
    if (!id) {
      setError(t('linkedIn.invalidPageId', 'Enter the numbers from your company page’s admin URL, or paste that URL.'));
      inputRef.current?.focus();
      return;
    }
    pendingRef.current = true;
    setSaving(true);
    setError('');
    try {
      await set(ref(database, `companies/${companyUID}/li_id`), id);
    } catch {
      setError(t('linkedIn.saveError', 'Couldn’t save your page. Please try again.'));
      pendingRef.current = false;
      setSaving(false);
      return;
    }
    pendingRef.current = false;
    setSaving(false);
    onSaved(id);
  };

  return createPortal(
    <div className="linkedin-setup-overlay" onClick={close}>
      <section className="linkedin-setup-dialog" role="dialog" aria-modal="true" aria-labelledby="linkedin-setup-title"
        aria-describedby="linkedin-setup-intro" dir={isRTL ? 'rtl' : 'ltr'} ref={dialogRef}
        onClick={event => event.stopPropagation()} onKeyDown={handleKeys}>
        <header className="linkedin-setup-header">
          <span className="linkedin-setup-mark" aria-hidden="true">{uiText("in")}</span>
          <button type="button" className="linkedin-setup-close" onClick={close} disabled={saving}
            aria-label={t('linkedIn.closeSetup', 'Close LinkedIn setup')}><i className="fa-solid fa-xmark" aria-hidden="true" /></button>
        </header>
        <h2 id="linkedin-setup-title">{t('linkedIn.setupTitle', 'Connect your company page')}</h2>
        <p id="linkedin-setup-intro">{t('linkedIn.setupIntro', 'Add your LinkedIn company page to set up job posting.')}</p>
        <form onSubmit={save}>
          <label htmlFor="linkedin-page-id">{t('linkedIn.pageIdLabel', 'Company page ID or admin URL')}</label>
          <input id="linkedin-page-id" ref={inputRef} value={value} disabled={saving}
            placeholder={t('linkedIn.pageIdPlaceholder', 'e.g. 12345678')}
            onChange={event => { setValue(event.target.value); setError(''); }}
            aria-invalid={Boolean(error)} aria-describedby={error ? 'linkedin-setup-error' : 'linkedin-setup-help'} />
          {error && <p id="linkedin-setup-error" className="linkedin-setup-error" role="alert">{uiText(error)}</p>}
          <div className="linkedin-setup-help" id="linkedin-setup-help">
            <h3>{t('linkedIn.findPageId', 'Where do I find it?')}</h3>
            <p>{t('linkedIn.findPageIdHint', 'Open your company page on LinkedIn as an admin. Copy the numbers after /company/ in the address bar.')}</p>
            <div className="linkedin-setup-url" dir="ltr">linkedin.com/company/<strong>12345678</strong>/admin</div>
            <a href="https://www.linkedin.com/help/linkedin/answer/a415420/associate-your-linkedin-company-id-with-the-linkedin-job-board-faqs"
              target="_blank" rel="noopener noreferrer">{t('linkedIn.getHelp', 'Help finding your page ID')} <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" /></a>
          </div>
          {!hasAtsAccess && <p className="linkedin-setup-plan-note"><i className="fa-solid fa-circle-info" aria-hidden="true" />{t('linkedIn.atsNote', 'You can save your page now. LinkedIn job syncing requires an ATS plan.')}</p>}
          <footer className="linkedin-setup-actions">
            <button type="button" className="linkedin-setup-secondary" onClick={close} disabled={saving}>{t('linkedIn.notNow', 'Not now')}</button>
            <button type="submit" className="linkedin-setup-primary" disabled={saving || !value.trim() || !companyUID}>
              {saving ? t('linkedIn.saving', 'Saving…') : t('linkedIn.savePage', 'Save company page')}
            </button>
          </footer>
        </form>
      </section>
    </div>, document.body
  );
}
